import { NavLink, useLocation } from 'react-router-dom';

import { useAuth } from '../../auth/authContext.js';
import { navSections } from '../../navigation.js';
import { useQuickAccess } from '../../features/quick-access/QuickAccessProvider.js';
import { CatalystLogo } from '../ui/CatalystLogo.js';
import { Icon } from '../ui/Icon.js';

interface SidebarProps {
  /** Called when a nav link is activated (used to close the drawer on mobile). */
  onNavigate?: () => void;
}

export function Sidebar({ onNavigate }: SidebarProps): JSX.Element {
  const auth = useAuth();
  const { isFavorite, toggleFavorite } = useQuickAccess();
  const location = useLocation();

  // Some menu items share a route and differ only by query string (Support vs Project Tickets),
  // so the default path-only NavLink matching would highlight both at once.
  const isItemActive = (to: string): boolean => {
    const [path, queryString] = to.split('?');
    const pathActive =
      to === '/'
        ? location.pathname === '/'
        : location.pathname === path || location.pathname.startsWith(`${path}/`);
    if (!pathActive) return false;
    if (!queryString) return true;
    const current = new URLSearchParams(location.search);
    return [...new URLSearchParams(queryString)].every(
      ([key, value]) => current.get(key) === value,
    );
  };

  return (
    <nav className="flex h-full flex-col gap-6 overflow-y-auto px-3 py-4">
      <div className="flex items-center gap-2 px-2">
        <CatalystLogo className="h-8 w-auto text-white" />
        <span className="text-xl font-light tracking-wide text-white/80">PSA</span>
      </div>

      <div className="flex flex-col gap-5">
        {navSections.map((section, index) => {
          const items = section.items.filter((item) =>
            item.key === undefined
              ? (item.isVisible?.(auth) ?? true)
              : auth.canSeeMenu(item.key),
          );
          if (items.length === 0) {
            return null;
          }

          return (
            <div key={section.heading ?? `section-${index}`} className="flex flex-col gap-1">
              {section.heading ? (
                <p className="px-3 text-xs font-semibold uppercase tracking-wider text-white/50">
                  {section.heading}
                </p>
              ) : null}
              {items.map((item) => {
                const favKey = `menu:${item.key ?? item.to}`;
                const favorited = isFavorite(favKey);
                return (
                  <div key={item.to} className="group relative flex items-center">
                    <NavLink
                      to={item.to}
                      end={item.to === '/'}
                      onClick={onNavigate}
                      className={`flex flex-1 items-center gap-3 rounded-lg py-2 pl-3 pr-9 text-sm font-medium transition ${
                        isItemActive(item.to)
                          ? 'bg-white/20 text-white'
                          : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <Icon name={item.icon} className="h-5 w-5 shrink-0" />
                      {item.label}
                    </NavLink>
                    <button
                      type="button"
                      onClick={() =>
                        toggleFavorite({
                          kind: 'menu',
                          key: item.key ?? item.to,
                          label: item.label,
                          path: item.to,
                          icon: item.icon,
                        })
                      }
                      className={`absolute right-2 rounded p-1 transition ${
                        favorited
                          ? 'text-amber-300'
                          : 'text-white/40 opacity-0 hover:text-amber-300 group-hover:opacity-100'
                      }`}
                      title={favorited ? 'Remove from favorites' : 'Add to favorites'}
                      aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <Icon
                        name="star"
                        className="h-4 w-4"
                        fill={favorited ? 'currentColor' : 'none'}
                      />
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
